import { useTranslation } from "react-i18next";
import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { FONT } from "@plannel/components/ai/typographyTokens";
import BaseCostTooltip from "./BaseCostTooltip";
import LlmUsageBreakdownCard from "./LlmUsageBreakdownCard";
import LlmUsageTrendChart from "./LlmUsageTrendChart";
import StatusChip from "./StatusChip";
import {
  CARD_SX, NUM_SX, TOKEN_KEYS, TOKEN_TYPE_LABEL_KEYS, UC, baseCostText, chart, fmtBase, fmtCost, fmtDec1, fmtInt, num,
  seriesColor,
} from "./llmUsageUtil";

// 폭 판정은 뷰포트가 아니라 내용 폭 — 사이드바·챗 패널 개폐에도 맞는다
const RIGHT_W = 600;
const RIGHT_W_NARROW = 540;
const NARROW_BELOW = 1160;
const STACK_BELOW = 1000;
const TREND_MIN_H = 240;

// 기간의 모든 날짜 수 — 호출 없는 날도 일평균 분모에 넣는다
const periodDayCount = (from, to) => {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  return Number.isNaN(start) || Number.isNaN(end) || end < start ? 0 : Math.round((end - start) / 86400000) + 1;
};

const KpiCard = ({ label, children, subs, accent }) => (
  <Box sx={{ ...CARD_SX, justifyContent: "center", minHeight: 88, p: "12px 16px",
    ...(accent ? { bgcolor: UC.accBg, borderColor: UC.accBorder } : null) }}>
    <Box sx={{ fontSize: FONT.BODY, color: UC.text2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</Box>
    <Box sx={{ ...NUM_SX, fontSize: FONT.KPI, fontWeight: 700, lineHeight: 1.2, letterSpacing: "-0.02em",
      color: accent ? UC.accText : UC.text1, my: "2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
      {children}
    </Box>
    {/* 폭이 모자라면 항목 단위로 줄바꿈한다. 높이는 칩이 든 카드에 맞춰 예약해 세 장의 숫자가 한 선에 선다 */}
    <Box sx={{ ...NUM_SX, display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 1.25,
      minHeight: 20, fontSize: FONT.CAPTION, color: UC.text3 }}>
      {subs.filter(Boolean).map((s, i) => <Box key={i} component="span" sx={{ whiteSpace: "nowrap" }}>{s}</Box>)}
    </Box>
  </Box>
);

// 오류 수 — 건수가 있을 때만 상세(오류만) 로 넘어가는 칩. 0 건이면 흐린 글자, 말은 양쪽 같은 키로
const ErrorSub = ({ t, count, onClick }) => {
  // 어순은 번역에 맡긴다(en 은 "Errors: 2"). count 는 자릿수 구분 문자열이라 i18next 복수형 분기를 타지 않는다
  const text = t("llmUsageErrorCount", { count: fmtInt(count) });
  if (count <= 0) return text;
  return <StatusChip value="error" label={text} fontSize={FONT.CAPTION} onClick={onClick} />;
};

const TokenTypeCard = ({ k, t }) => {
  const total = num(k.total_tokens);
  const pct = (v) => (total > 0 ? (num(v) / total) * 100 : 0);
  return (
    <Box sx={{ ...CARD_SX, flexShrink: 0 }}>
      <Box component="h3" sx={{ m: 0, mb: 1.25, fontSize: FONT.TITLE, fontWeight: 600, color: UC.text1 }}>{t("llmUsageTokenShare")}</Box>
      <Box sx={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", bgcolor: chart.barTrack }}>
        {TOKEN_KEYS.map((key) => (num(k[key]) > 0 ? <Box key={key} sx={{ flex: num(k[key]), bgcolor: seriesColor(key) }} /> : null))}
      </Box>
      <Box sx={{ mt: 1.5, display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", columnGap: 1.5 }}>
        {TOKEN_KEYS.map((key) => (
          <Box key={key} sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: FONT.CAPTION, color: UC.text2 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: seriesColor(key), flexShrink: 0 }} />
              <Box component="span" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {t(TOKEN_TYPE_LABEL_KEYS[key])}
              </Box>
            </Box>
            <Box sx={{ ...NUM_SX, mt: 0.25, fontSize: FONT.TITLE, fontWeight: 600, color: UC.text1 }}>{fmtInt(k[key])}</Box>
            <Box sx={{ ...NUM_SX, fontSize: FONT.CAPTION, color: UC.text3 }}>{`${fmtDec1(pct(k[key]))}%`}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const LlmUsageOverviewTab = ({ summary, loading, onGoPrices, onGoErrors, breakdownProps }) => {
  const { t } = useTranslation();

  if (!summary) {
    return (
      <Box sx={{ height: "100%", display: "flex", justifyContent: "center", pt: 3 }}>
        {loading && <CircularProgress size={22} />}
      </Box>
    );
  }
  const k = summary.kpi || {};
  const costs = k.costs || [];
  const unpricedModels = summary.unpriced_models || [];
  const days = periodDayCount(summary.from_dt, summary.to_dt);
  const callsPerDay = days ? num(k.call_cnt) / days : 0;
  const total = num(k.total_tokens);
  const cacheShare = total > 0 ? ((num(k.cache_read_tokens) + num(k.cache_write_tokens)) / total) * 100 : 0;
  // 호출당 비용은 단가가 붙은 호출만 분모 — 통화가 여럿이면 통화별 호출 수를 몰라 생략
  const unpricedCalls = num(k.unpriced_call_cnt);
  const pricedCalls = num(k.call_cnt) - unpricedCalls;
  const costPerCall = costs.length === 1 && pricedCalls > 0 ? num(costs[0].cost_amt) / pricedCalls : null;
  // 통화가 하나일 때만 비용선을 그린다 — 여럿이면 합산할 수 없어 한 통화만 그리면 나머지가 빠진다
  const lineCurrency = costs.length === 1 ? costs[0].currency_cd : null;
  const baseCurrency = summary.base_currency_cd;
  const baseText = baseCostText(baseCurrency, k);

  const narrow = `@container (max-width: ${NARROW_BELOW - 1}px)`;
  const stacked = `@container (max-width: ${STACK_BELOW - 1}px)`;

  return (
    <Box sx={{ height: "100%", containerType: "inline-size", overflow: "auto" }}>
      <Box sx={{ height: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: 1.5,
        p: "16px 20px 20px", minHeight: 88 + 16 + TREND_MIN_H + 36, opacity: loading ? 0.6 : 1, transition: "opacity .15s",
        [stacked]: { height: "auto" } }}>
        {unpricedModels.length > 0 && (
          <Alert severity="warning" sx={{ fontSize: FONT.BODY, py: 0, flexShrink: 0 }}
            action={<Button size="small" onClick={onGoPrices}>{t("llmUsageGoRegisterPrice")}</Button>}>
            {t("llmUsageUnpricedWarning", { calls: fmtInt(unpricedCalls), models: unpricedModels.length })}
          </Alert>
        )}

        <Box sx={{
          flex: 1, minHeight: 0, display: "grid", gap: "16px",
          gridTemplateColumns: `minmax(0, 1fr) ${RIGHT_W}px`, gridTemplateRows: "minmax(0, 1fr)",
          [narrow]: { gridTemplateColumns: `minmax(0, 1fr) ${RIGHT_W_NARROW}px` },
          [stacked]: { flex: "none", gridTemplateColumns: "minmax(0, 1fr)", gridTemplateRows: "auto" },
        }}>
          <Box sx={{ minWidth: 0, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", gap: "16px",
            [stacked]: { overflow: "visible" } }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "16px", flexShrink: 0 }}>
              <KpiCard label={t("llmUsageCalls")}
                subs={[
                  <ErrorSub t={t} count={num(k.error_cnt)} onClick={onGoErrors} />,
                  t("llmUsageCallsPerDay", { count: fmtDec1(callsPerDay) }),
                ]}>
                {fmtInt(k.call_cnt)}
              </KpiCard>
              <KpiCard label={t("llmUsageTotalTokens")} subs={[t("llmUsageCacheShare", { pct: fmtDec1(cacheShare) })]}>
                {fmtInt(total)}
              </KpiCard>
              <KpiCard accent label={t("llmUsageEstCost")}
                subs={[
                  costPerCall != null && t("llmUsageCostPerCall", { amount: fmtBase(costPerCall, lineCurrency) }),
                  unpricedCalls > 0 && t("llmUsageUnpricedExcluded", { count: fmtInt(unpricedCalls) }),
                ]}>
                {/* 환산액은 금액 오른쪽에 두고, 폭이 모자라면 잘리지 않게 다음 줄로 내린다 */}
                <Box component="span" sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: 1 }}>
                  <Box component="span" sx={{ minWidth: 0, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {costs.length === 0 && "-"}
                    {costs.length === 1 && (
                      <>
                        <Box component="span" sx={{ fontSize: FONT.TITLE, fontWeight: 600, mr: 0.5 }}>{lineCurrency}</Box>
                        {fmtCost(costs[0].cost_amt, lineCurrency)}
                      </>
                    )}
                    {costs.length > 1 && (
                      <Box component="span" sx={{ fontSize: FONT.TITLE }}>{costs.map((c) => fmtBase(c.cost_amt, c.currency_cd)).join(" / ")}</Box>
                    )}
                  </Box>
                  {baseText && (
                    // 글자 크기는 달라도 USD 금액과 밑선(베이스라인)을 맞춘다 — (i) 아이콘만 세로 가운데
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline",
                      fontSize: FONT.SUB, fontWeight: 400, letterSpacing: 0, color: UC.text3 }}>
                      {baseText}
                      <BaseCostTooltip baseCurrency={baseCurrency} rates={k.base_rates} baseTotal={k.base_cost_amt} />
                    </Box>
                  )}
                </Box>
              </KpiCard>
            </Box>
            <Box sx={{ ...CARD_SX, flex: 1, minHeight: TREND_MIN_H, [stacked]: { flex: "none", height: 320 } }}>
              <LlmUsageTrendChart daily={summary.daily} currency={lineCurrency} baseCurrency={baseCurrency} />
            </Box>
          </Box>

          <Box sx={{ minWidth: 0, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", gap: "16px",
            [stacked]: { overflow: "visible" } }}>
            <LlmUsageBreakdownCard summary={summary} {...breakdownProps}
              sx={{ flex: 1, minHeight: 0, [stacked]: { flex: "none", height: 320 } }} />
            <TokenTypeCard k={k} t={t} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LlmUsageOverviewTab;
